<template>
  <div class="video-container">
    <!-- Navbar -->
    <nav class="nav-bar">
      <div class="nav-left">
        <img
          src="@/assets/images/logot.svg"
          class="logo"
          alt="decast"
          @click="$router.push('/')"
        />
        <h1 class="recording-title">{{ displayTitle }}</h1>
        <ContentCredentials v-if="prov" :provenance="prov" class="ml-3" />
      </div>
      <div class="nav-center">
        <div class="global-search">
          <input
            v-model="globalSearchQuery"
            @input="debouncedGlobalSearch"
            type="text"
            placeholder="Search across transcript and summary..."
            class="global-search-input"
          />
          <div
            class="global-search-controls"
            v-if="globalSearchQuery && globalMatchCount > 0"
          >
            <span class="global-match-count"
              >{{ globalMatchCount }} matches</span
            >
            <div class="global-navigation-arrows">
              <button
                @click="navigateGlobalMatch(-1)"
                class="global-arrow-btn"
                title="Previous match"
              >
                ↑
              </button>
              <button
                @click="navigateGlobalMatch(1)"
                class="global-arrow-btn"
                title="Next match"
              >
                ↓
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="nav-right">
        <button class="share-btn" @click="shareRecording">
          <img src="@/assets/images/share.svg" /> Share
        </button>
      </div>
    </nav>

    <!-- Main Content -->
    <div v-if="videoUrl" class="main-content">
      <div class="content-grid">
        <!-- Video Player -->
        <div class="video-section">
          <video ref="videoPlayer" class="video-player" controls>
            <source :src="videoUrl" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        <!-- Summary Panel -->
        <div class="summary-panel panel">
          <div class="panel-header">
            <h2 class="panel-title">Summary & Key Points</h2>
            <div v-if="summary" class="translate-controls">
              <select
                v-model="selectedLanguage"
                @change="handleLanguageChange"
                class="translate-select"
              >
                <option class="text-green-500" value="">Original</option>
                <option v-for="lang in languages" :key="lang" :value="lang">
                  {{ lang }}
                </option>
              </select>
              <span v-if="isTranslating" class="translating-pill"
                >Translating…</span
              >
            </div>
            <div class="download-options" v-if="summary">
              <img
                @click="downloadSummary('txt')"
                class="download-btn"
                src="@/assets/images/down.png"
                title="Download as TXT"
              />
              <img
                @click="downloadSummary('json')"
                class="download-btn"
                src="@/assets/images/json.png"
                title="Download as JSON"
              />
            </div>
          </div>
          <div class="search-bar">
            <input
              v-model="searchQuery2"
              type="text"
              placeholder="Search summary..."
              @input="debouncedSearch2"
              ref="summarySearchInput"
            />
            <div class="search-controls">
              <span class="match-count" v-if="searchQuery2"
                >{{ matchCount2 }} matches</span
              >
              <div
                class="navigation-arrows"
                v-if="searchQuery2 && matchCount2 > 0"
              >
                <button
                  @click="navigateSummaryMatch(-1)"
                  class="arrow-btn"
                  title="Previous match"
                >
                  ↑
                </button>
                <button
                  @click="navigateSummaryMatch(1)"
                  class="arrow-btn"
                  title="Next match"
                >
                  ↓
                </button>
              </div>
            </div>
          </div>
          <div class="panel-content" v-if="loading === 'checking'">
            <div class="spinner">
              <img
                src="@/assets/images/logot.svg"
                class="spinner-logo"
                alt="decast"
              />
              <p>Checking existing summary...</p>
            </div>
          </div>
          <div class="panel-content" v-if="loading === 'starting'">
            <div class="spinner">
              <img
                src="@/assets/images/logot.svg"
                class="spinner-logo"
                alt="decast"
              />
              <p>Starting summary process...</p>
            </div>
          </div>
          <div class="panel-content" v-if="loading === 'processing'">
            <div class="spinner">
              <img
                src="@/assets/images/logot.svg"
                class="spinner-logo"
                alt="decast"
              />
              <p>Processing summary...</p>
            </div>
          </div>
          <div
            class="panel-content formatted-text"
            ref="summaryContainer"
            v-else
            v-html="highlightedSummary"
          ></div>
        </div>

        <!-- Transcription Panel -->
        <div class="transcription-panel panel">
          <div class="panel-header">
            <h2 class="panel-title flex w-full justify-between items-center">
              Transcription Experimental
              <span class="text-green-500 mr-6"
                >Detected Language: {{ detectedLanguage }}</span
              >
            </h2>
            <div class="download-options" v-if="speakers">
              <img
                @click="downloadTranScript('txt')"
                class="download-btn"
                src="@/assets/images/down.png"
                title="Download as TXT"
              />
              <img
                @click="downloadTranScript('json')"
                class="download-btn"
                src="@/assets/images/json.png"
                title="Download as JSON"
              />
            </div>
          </div>
          <div class="search-bar">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search transcription..."
              @input="debouncedSearch"
              ref="searchInput"
            />
            <div class="search-controls">
              <span class="match-count" v-if="searchQuery"
                >{{ matchCount }} matches</span
              >
              <div
                class="navigation-arrows"
                v-if="searchQuery && matchCount > 0"
              >
                <button
                  @click="navigateTranscriptMatch(-1)"
                  class="arrow-btn"
                  title="Previous match"
                >
                  ↑
                </button>
                <button
                  @click="navigateTranscriptMatch(1)"
                  class="arrow-btn"
                  title="Next match"
                >
                  ↓
                </button>
              </div>
            </div>
          </div>

          <div class="panel-content" v-if="loading === 'checking'">
            <div class="spinner">
              <img
                src="@/assets/images/logot.svg"
                class="spinner-logo"
                alt="decast"
              />
              <p>Checking existing transcription...</p>
            </div>
          </div>
          <div class="panel-content" v-if="loading === 'starting'">
            <div class="spinner">
              <img
                src="@/assets/images/logot.svg"
                class="spinner-logo"
                alt="decast"
              />
              <p>Starting transcription process...</p>
            </div>
          </div>
          <div class="panel-content" v-if="loading === 'processing'">
            <div class="spinner">
              <img
                src="@/assets/images/logot.svg"
                class="spinner-logo"
                alt="decast"
              />
              <p>Processing transcription, this is a one time process...</p>
            </div>
          </div>
          <div
            class="panel-content transcript-content"
            ref="transcriptionContainer"
            v-else
            v-html="highlightedTranscript"
          ></div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else class="loading-state">
      <div class="spinner">
        <img
          src="@/assets/images/logot.svg"
          class="spinner-logo"
          alt="decast"
        />
        <p>Loading recording...</p>
      </div>
    </div>
  </div>
</template>

<script>
import planAccount from '@/mixins/planAccount';
import axios from 'axios';
import debounce from 'lodash/debounce';
import constants from '../../../../../constant';
import ContentCredentials from '@/components/credentials/ContentCredentials.vue';
import { normalizeProvenance } from '@/utils/provenance';

export default {
  mixins: [planAccount],
  components: { ContentCredentials },
  name: 'RecordingPlay',
  data() {
    return {
      videoUrl: null,
      recording: null,
      speakers: null,
      summary: null,
      originalSummary: null,
      selectedLanguage: '',
      detectedLanguage: '',
      isTranslating: false,
      languages: [
        'English',
        'Arabic',
        'Chinese (Simplified)',
        'Chinese (Traditional)',
        'Dutch',
        'French',
        'German',
        'Hindi',
        'Italian',
        'Japanese',
        'Korean',
        'Portuguese',
        'Russian',
        'Spanish',
        'Turkish',
        'Vietnamese',
      ],
      translationCache: {},
      recording_id: '',
      startedFor: '',
      public_meeting_id: '',
      loading: false,
      taskId: null,
      pollingInterval: null,
      searchQuery: '',
      searchQuery2: '',
      matchCount: 0,
      matchCount2: 0,
      currentMatchIndex: -1,
      currentMatchIndex2: -1,
      globalSearchQuery: '',
      globalMatchCount: 0,
      globalCurrentMatchIndex: -1,
      globalMatches: [],
    };
  },
  created() {
    this.initializeRecording();
    this.debouncedSearch = debounce(this.handleSearch, 300);
    this.debouncedSearch2 = debounce(this.handleSearch2, 300);
    this.debouncedGlobalSearch = debounce(this.handleGlobalSearch, 300);
    const recordingId = this.$route.params.recordingId;
  },
  computed: {
    formattedSummary() {
      return this.formatSummary(this.summary);
    },
    formattedTranscript() {
      return this.formatParagraphs(this.highlightSpeakers(this.speakers));
    },
    highlightedTranscript() {
      // Use global search query if active and no local search
      const query =
        this.globalSearchQuery && !this.searchQuery
          ? this.globalSearchQuery
          : this.searchQuery;
      return this.searchInTranscript(this.formattedTranscript, query);
    },
    highlightedSummary() {
      // Use global search query if active and no local search
      const query =
        this.globalSearchQuery && !this.searchQuery2
          ? this.globalSearchQuery
          : this.searchQuery2;
      return this.searchInSummary(this.formattedSummary, query);
    },
    displayTitle() {
      return this.recording?.Name || 'Recording Player';
    },
    prov() {
      return normalizeProvenance(this.recording);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$el.addEventListener('click', (e) => {
        if (e.target.classList.contains('timestamp')) {
          const timeText = e.target.innerText;
          const [hours, min, sec] = timeText.split(':').map(Number);
          const seconds = hours * 3600 + min * 60 + sec;
          this.seekTo(seconds);
        }
      });
    });

    // Add global function for copy functionality
    window.copyHeadingContent = this.copyHeadingContent;
  },
  beforeUnmount() {
    // Clean up global function
    if (window.copyHeadingContent) {
      delete window.copyHeadingContent;
    }
  },
  methods: {
    async initializeRecording() {
      this.recording = this.$store.state.cast.recording;

      if (this.recording) {
        this.recording_id = this.recording['Record ID'];
        this.public_meeting_id = this.recording.pub_id;
        await this.startTranscription();
      } else {
        console.error('No recording found in store.');
        const recordingIdFromPath = this.$route.params.recordingId;
        if (recordingIdFromPath) {
          this.recording_id = recordingIdFromPath;
          // Opened by link: the meeting id is not in the store. Take it from
          // the URL (meeting-ended passes ?pub=) or from the user's own
          // recordings list; without it the transcript cannot name speakers.
          this.public_meeting_id = this.$route.query.pub || null;
          if (!this.public_meeting_id) {
            this.public_meeting_id = await this.lookupPublicMeetingId(
              recordingIdFromPath
            );
          }
          await this.startTranscription();
        } else {
          this.$router.push({ name: 'Welcome to Dashbord' });
        }
      }
      this.videoUrl = `${this.$globals.roomDomain}/playback/video/${this.recording_id}/video-0.m4v`;
    },
    async lookupPublicMeetingId(recordId) {
      if (!localStorage.getItem('accessToken')) return null;
      try {
        const list = await this.$store.dispatch('cast/recordings');
        const hit = (list || []).find((r) => r && r['Record ID'] === recordId);
        return (hit && hit.pub_id) || null;
      } catch (e) {
        return null;
      }
    },
    async shouldRedoNameless(data) {
      const text = `${data.speaker_identified_text || ''}`;
      if (!/\bPerson \d+\b/.test(text)) return false;
      // The recording server publishes talking.json (who spoke when) next to
      // the video; with it the service can name speakers without a meeting id.
      try {
        const talking = await axios.get(
          `${this.$globals.roomDomain}/playback/video/${this.recording_id}/talking.json`
        );
        if (
          talking.data &&
          Array.isArray(talking.data.segments) &&
          talking.data.segments.length
        )
          return true;
      } catch (e) {
        // no talking.json for this recording
      }
      if (!this.public_meeting_id) return false;
      try {
        const res = await axios.get(
          `${this.$globals.transcriptionBaseUrl}/api/audio/participants/${this.public_meeting_id}/`
        );
        const names = res.data && res.data.participants;
        return Array.isArray(names) && names.length > 0;
      } catch (e) {
        return false;
      }
    },
    showExisting(data) {
      this.transcript = data.transcription;
      this.summary = data.summary;
      this.originalSummary = data.summary;
      this.speakers = data.speaker_identified_text;
      this.detectedLanguage = data.detected_lang || 'unknown';
      this.autoSelectDetectedLanguage();
      this.loading = false;
    },
    async startTranscription() {
      if (!this.recording_id) {
        console.error('No recording ID provided');
        return;
      }
      // opening the same recording again is not a reason to ask for it again
      if (this.startedFor === this.recording_id) return;
      this.startedFor = this.recording_id;

      this.loading = 'checking';
      try {
        const checkResponse = await axios.get(
          `${this.$globals.transcriptionBaseUrl}/api/audio/transcription/${this.recording_id}/`
        );
        console.log(checkResponse, 'check');
        if (
          checkResponse.data.transcription &&
          (await this.shouldRedoNameless(checkResponse.data))
        ) {
          // Transcribed without a meeting id, so speakers are "Person N".
          // Names exist now: drop it and let a fresh run label them.
          // Only when a fresh run is actually possible: deleting first and
          // discovering the account has no AI hours left would destroy the
          // transcript it was trying to improve.
          if (this.aiBlocked) {
            this.showExisting(checkResponse.data);
            return;
          }
          await axios.delete(
            `${this.$globals.transcriptionBaseUrl}/api/audio/delete/${this.recording_id}/`
          );
        } else if (checkResponse.data.transcription) {
          this.showExisting(checkResponse.data);
          return;
        }
      } catch (error) {
        console.log(
          'No existing transcription found, proceeding with new request.'
        );
      }

      // Reading an existing transcript is never blocked; only making a new one is,
      // and an account with no hours left should be told rather than left spinning.
      if (this.aiBlocked) {
        this.loading = false;
        this.error = this.aiBlockedMessage;
        return;
      }
      this.loading = 'starting';
      try {
        const response = await axios.post(
          `${this.$globals.transcriptionBaseUrl}/api/audio/upload/`,
          {
            recordingId: this.recording_id,
            public_meeting_id: this.public_meeting_id,
          }
        );

        this.taskId = response.data.task_id;
        console.log('Task started with ID:', this.taskId);
        this.pollTaskStatus();
      } catch (error) {
        console.error('Error starting transcription:', error);
        this.loading = false;
      }
    },
    async pollTaskStatus() {
      if (!this.taskId) {
        console.error('No task ID available');
        this.loading = false;
        return;
      }

      this.loading = 'processing';
      this.pollingInterval = setInterval(async () => {
        try {
          const response = await axios.get(
            `${this.$globals.transcriptionBaseUrl}/api/audio/get_task_status/${this.taskId}/`
          );
          const { status, result, error } = response.data;

          if (status === 'SUCCESS') {
            this.transcript = result.transcription || '';
            this.summary = result.summary || '';
            this.originalSummary = result.summary || '';
            this.speakers = result.speaker_identified_text || '';
            this.detectedLanguage = result.detected_lang || 'unknown';
            this.autoSelectDetectedLanguage();
            localStorage.setItem('transcript', JSON.stringify(result));
            clearInterval(this.pollingInterval);
            this.loading = false;
          } else if (status === 'FAILURE') {
            console.error('Task failed:', error);
            clearInterval(this.pollingInterval);
            this.loading = false;
          }
        } catch (error) {
          console.error('Error polling task status:', error);
          clearInterval(this.pollingInterval);
          this.loading = false;
        }
      }, 10000);
    },
    async handleLanguageChange() {
      // If switching to original/empty, just restore original summary
      if (this.selectedLanguage === '') {
        this.summary = this.originalSummary || this.summary;
        return;
      }

      // If switching to detected language (original), restore without API call
      if (
        this.selectedLanguage === this.detectedLanguage &&
        this.originalSummary
      ) {
        this.summary = this.originalSummary;
        return;
      }

      // Use cache if present
      if (this.translationCache[this.selectedLanguage]) {
        this.summary = this.translationCache[this.selectedLanguage];
        return;
      }

      if (!this.originalSummary) return;

      try {
        this.isTranslating = true;
        const { data } = await axios.post(
          `${this.$globals.transcriptionBaseUrl}/api/audio/translate/`,
          {
            text: this.originalSummary,
            target_language: this.selectedLanguage,
          }
        );
        const translated = data?.translated || this.originalSummary;
        // cache & set
        this.translationCache[this.selectedLanguage] = translated;
        this.summary = translated;
      } catch (e) {
        console.error('Translation failed:', e);
        // fallback to original on error
        this.selectedLanguage = '';
        this.summary = this.originalSummary;
      } finally {
        this.isTranslating = false;
      }
    },
    autoSelectDetectedLanguage() {
      // Check if detected language matches any language in the dropdown
      if (!this.detectedLanguage || this.detectedLanguage === 'unknown') return;

      // Normalize detected language for comparison
      const detectedLangNormalized = this.detectedLanguage.toLowerCase();

      // Find matching language in dropdown
      const matchingLanguage = this.languages.find((lang) => {
        const langNormalized = lang.toLowerCase();
        // Check for exact match or partial match (e.g., "en" matches "English")
        return (
          langNormalized === detectedLangNormalized ||
          langNormalized.includes(detectedLangNormalized) ||
          detectedLangNormalized.includes(langNormalized)
        );
      });

      // If we found a match, select it as the default
      if (matchingLanguage) {
        this.selectedLanguage = matchingLanguage;
        // Cache original summary under the detected language to avoid API calls
        if (this.originalSummary) {
          this.translationCache[matchingLanguage] = this.originalSummary;
        }
        console.log(`Auto-selected detected language: ${matchingLanguage}`);
      }
    },
    handleSearch() {
      // Clear global search when using individual search
      this.clearGlobalSearch();

      this.currentMatchIndex = -1;
      this.$nextTick(() => {
        const container = this.$refs.transcriptionContainer;
        const matches = container?.querySelectorAll('.search-match');
        this.matchCount = matches?.length || 0;
        if (matches?.length) {
          this.currentMatchIndex = 0;
          matches[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
    handleSearch2() {
      // Clear global search when using individual search
      this.clearGlobalSearch();

      this.currentMatchIndex2 = -1;
      this.$nextTick(() => {
        const container = this.$refs.summaryContainer;
        const matches = container?.querySelectorAll('.search-match2');
        this.matchCount2 = matches?.length || 0;
        if (matches?.length) {
          this.currentMatchIndex2 = 0;
          matches[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
    searchInTranscript(text, query) {
      if (!query.trim()) return text;
      const regex = new RegExp(
        `(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
        'gi'
      );
      return text.replace(regex, '<mark class="search-match">$1</mark>');
    },
    searchInSummary(text, query) {
      if (!query.trim()) return text;
      const regex = new RegExp(
        `(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
        'gi'
      );
      return text.replace(regex, '<mark class="search-match2">$1</mark>');
    },
    navigateSummaryMatch(direction) {
      const container = this.$refs.summaryContainer;
      const matches = container?.querySelectorAll('.search-match2');
      if (!matches?.length) return;

      this.currentMatchIndex2 =
        (this.currentMatchIndex2 + direction + matches.length) % matches.length;
      matches[this.currentMatchIndex2].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    },
    navigateTranscriptMatch(direction) {
      const container = this.$refs.transcriptionContainer;
      const matches = container?.querySelectorAll('.search-match');
      if (!matches?.length) return;

      this.currentMatchIndex =
        (this.currentMatchIndex + direction + matches.length) % matches.length;
      matches[this.currentMatchIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    },
    handleGlobalSearch() {
      if (!this.globalSearchQuery.trim()) {
        this.globalMatches = [];
        this.globalMatchCount = 0;
        this.globalCurrentMatchIndex = -1;
        return;
      }

      this.globalCurrentMatchIndex = -1;
      this.globalMatches = [];

      this.$nextTick(() => {
        const transcriptContainer = this.$refs.transcriptionContainer;
        const summaryContainer = this.$refs.summaryContainer;

        const transcriptMatches =
          transcriptContainer?.querySelectorAll('.search-match') || [];
        const summaryMatches =
          summaryContainer?.querySelectorAll('.search-match2') || [];

        // Create array of all matches with their source information
        transcriptMatches.forEach((match, index) => {
          this.globalMatches.push({
            element: match,
            container: transcriptContainer,
            panel: 'transcript',
            index: index,
          });
        });

        summaryMatches.forEach((match, index) => {
          this.globalMatches.push({
            element: match,
            container: summaryContainer,
            panel: 'summary',
            index: index,
          });
        });

        this.globalMatchCount = this.globalMatches.length;

        // Jump to first match
        if (this.globalMatches.length > 0) {
          this.globalCurrentMatchIndex = 0;
          this.scrollToGlobalMatch(0);
        }
      });
    },
    clearGlobalSearch() {
      this.globalSearchQuery = '';
      this.globalMatches = [];
      this.globalMatchCount = 0;
      this.globalCurrentMatchIndex = -1;

      // Remove global highlighting
      this.globalMatches.forEach((m) => {
        m.element.classList.remove('focused-global-match');
      });
    },
    navigateGlobalMatch(direction) {
      if (this.globalMatches.length === 0) return;

      this.globalCurrentMatchIndex =
        (this.globalCurrentMatchIndex + direction + this.globalMatches.length) %
        this.globalMatches.length;
      this.scrollToGlobalMatch(this.globalCurrentMatchIndex);
    },
    scrollToGlobalMatch(index) {
      if (!this.globalMatches[index]) return;

      const match = this.globalMatches[index];

      // Remove previous highlight classes
      this.globalMatches.forEach((m) => {
        m.element.classList.remove('focused-global-match');
      });

      // Add highlight to current match
      match.element.classList.add('focused-global-match');
      match.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    toggleFullScreen() {
      const iframe = this.$el.querySelector('.video-player');
      if (iframe.requestFullscreen) {
        iframe.requestFullscreen();
      }
    },
    shareRecording() {
      navigator
        .share({
          title: this.recording?.Name || 'Recording Player',
          url: `${this.$globals.clientDomain}/play-recording/${this.recording_id}`,
        })
        .catch((err) => console.error('Share failed:', err));
    },
    downloadTranScript(format) {
      if (!this.transcript) return;
      let content, blob, filename;
      if (format === 'txt') {
        content = this.transcript;
        blob = new Blob([content], { type: 'text/plain' });
        filename = 'transcription.txt';
      } else if (format === 'json') {
        content = JSON.stringify(
          { transcription: this.transcript, summary: this.summary },
          null,
          2
        );
        blob = new Blob([content], { type: 'application/json' });
        filename = 'transcription.json';
      }
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    downloadSummary(format) {
      if (!this.summary) return;
      let content, blob, filename;
      if (format === 'txt') {
        content = this.summary;
        blob = new Blob([content], { type: 'text/plain' });
        filename = 'summary.txt';
      } else if (format === 'json') {
        content = JSON.stringify(
          { transcription: this.transcript, summary: this.summary },
          null,
          2
        );
        blob = new Blob([content], { type: 'application/json' });
        filename = 'summary.json';
      }
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    formatSummary(text) {
      if (!text) return '';

      // Replace timestamps with clickable spans
      text = text.replace(
        /\[(\d{2}:\d{2})\]/g,
        '<span class="timestamp text-grey-500 font-semibold cursor-pointer">[$1]</span>'
      );

      const lines = text.split('\n').map((line) => line.trim());
      const result = [];
      let inList = false;

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Skip empty lines but keep processing
        if (!line) {
          if (inList) {
            result.push('</ul>');
            inList = false;
          }
          continue;
        }

        // Handle separator dashes (--- or more)
        if (/^-{3,}$/.test(line)) {
          if (inList) {
            result.push('</ul>');
            inList = false;
          }
          result.push('<hr class="my-4 border-gray-600">');
          continue;
        }

        // Handle headings (### Key Points:)
        if (line.startsWith('###')) {
          if (inList) {
            result.push('</ul>');
            inList = false;
          }
          const headingText = line.replace(/^###\s*/, '');
          const headingId = `heading-${Math.random()
            .toString(36)
            .substr(2, 9)}`;
          result.push(`
                        <div class="heading-with-copy flex items-center justify-between my-4">
                            <h3 class="text-lg font-semibold text-green-500" id="${headingId}">${headingText}</h3>
                            <div class="copy-heading-btn" onclick="copyHeadingContent('${headingId}')" title="Copy section">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 5H6C5.44772 5 5 5.44772 5 6V18C5 18.5523 5.44772 19 6 19H18C18.5523 19 19 18.4477 19 18V6C19 5.44772 18.5523 5 18 5H16" stroke="currentColor" stroke-width="1.5"/>
                                    <rect x="8" y="3" width="8" height="4" rx="1" stroke="currentColor" stroke-width="1.5"/>
                                </svg>
                            </div>
                        </div>
                    `);
          continue;
        }

        // Handle bullet points (- item) but check for headings first
        if (line.startsWith('- ')) {
          const bulletContent = line.substring(2).trim();

          // If the content starts with bold text and ends with colon, treat it as a heading
          if (/^\*\*(.+?)\*\*:?\s*$/.test(bulletContent)) {
            if (inList) {
              result.push('</ul>');
              inList = false;
            }
            const headingText = bulletContent.replace(
              /^\*\*(.+?)\*\*(:?\s*)$/,
              '$1$2'
            );
            result.push(
              `<h4 class="text-base font-bold my-3 text-yellow-500">${headingText}</h4>`
            );
            continue;
          }

          // Otherwise, treat as regular bullet point
          if (!inList) {
            result.push(
              '<ul class="list-disc list-inside ml-4 mb-4 space-y-2">'
            );
            inList = true;
          }
          const formatted = bulletContent.replace(
            /\*\*(.+?)\*\*/g,
            '<span class="text-yellow-500 font-bold">$1</span>'
          );
          result.push(`<li class="ml-2">${formatted}</li>`);
          continue;
        }

        // Handle regular paragraphs
        if (inList) {
          result.push('</ul>');
          inList = false;
        }

        // Convert **bold text** to yellow bold
        const formatted = line.replace(
          /\*\*(.+?)\*\*/g,
          '<span class="text-yellow-500 font-bold">$1</span>'
        );
        result.push(`<p class="mb-2">${formatted}</p>`);
      }

      // Close any open list
      if (inList) {
        result.push('</ul>');
      }

      return result.join('');
    },
    formatParagraphs(text) {
      if (!text) return '';

      const parts = text.split(/\[(\d{2}:\d{2}:\d{2})\]/g);
      let html = '';
      let lastValidTimestamp = null;

      for (let i = 1; i < parts.length; i += 2) {
        const timestamp = parts[i];
        const paragraph = parts[i + 1]?.trim();

        if (
          timestamp &&
          paragraph &&
          paragraph.replace(/<[^>]+>/g, '').trim()
        ) {
          if (
            lastValidTimestamp !== timestamp ||
            paragraph !== parts[i - 1]?.trim()
          ) {
            html += `
                            <div class="timestamp-paragraph flex items-start mb-3">
                                <span class="timestamp text-blue-200 font-bold cursor-pointer mr-2">${timestamp}</span>
                                <span class="paragraph-text mb-3">${paragraph}</span>
                            </div>
                        `;
            lastValidTimestamp = timestamp;
          }
        }
      }

      return html;
    },
    handleTimestampClick(timeText) {
      console.log('Timestamp clicked:', timeText);
      const [hours, min, sec] = timeText.split(':').map(Number);
      const seconds = hours * 3600 + min * 60 + sec;
      this.seekTo(seconds);
    },
    seekTo(seconds) {
      console.log('SeekTo called with:', seconds);
      console.log('Video player ref:', this.$refs.videoPlayer);
      const player = this.$refs.videoPlayer;
      if (player) {
        player.currentTime = seconds;
        player.play();
      } else {
        console.error('Video player not found');
      }
    },
    highlightSpeakers(text) {
      if (!text) return '';
      const speakerColors = {};
      const colors = [
        'text-red-500',
        'text-blue-500',
        'text-green-500',
        'text-cyan-300',
        'text-pink-400',
        'text-purple-500',
        'text-pink-500',
        'text-orange-500',
      ];
      let colorIndex = 0;

      return text.replace(/<b>(.*?)<\/b>/g, (match, speaker) => {
        if (!speakerColors[speaker]) {
          speakerColors[speaker] = colors[colorIndex % colors.length];
          colorIndex++;
        }
        return `<span class="${speakerColors[speaker]} font-bold drop-shadow-[0_0_2px_rgba(255,255,255,0.3)]">${speaker}</span>`;
      });
    },
    formatSpeakers(text) {
      if (!text) return '';
      const lines = text.split('\n');
      const seenInitials = {};

      return lines
        .filter((line) => {
          const cleanLine = line.replace(/<[^>]+>/g, '').trim();
          return (
            cleanLine &&
            !line.match(/^\s*<b>.*<\/b>\s*(:)?\s*$/) &&
            !line.match(/^\s*\d{2}:\d{2}:\d{2}\s*$/)
          );
        })
        .map((line) => {
          const match = line.match(/<b>(.*?)<\/b>/);
          if (match) {
            const name = match[1];
            const initial = name.charAt(0).toUpperCase();
            if (!seenInitials[initial]) {
              seenInitials[initial] = `hsl(${Math.random() * 360}, 60%, 70%)`;
            }
            return `<div class='speaker-block'>
                            <div class='avatar' style='background: ${
                              seenInitials[initial]
                            };'>${initial}</div>
                            <div>
                                <p class='speaker-name'>${name}</p>
                                <p class='speaker-text'>${line
                                  .replace(match[0], '')
                                  .trim()}</p>
                            </div>
                        </div>`;
          }
          return `<p class='speaker-text'>${line.trim()}</p>`;
        })
        .join('\n');
    },
    copyHeadingContent(headingId) {
      try {
        const headingElement = document.getElementById(headingId);
        if (!headingElement) return;

        const headingText = headingElement.innerText;
        let htmlContent = `<h3 style="color: #22C55E; font-size: 1.125rem; font-weight: 600; margin: 1rem 0;">${headingText}</h3>\n`;
        let plainTextContent = headingText + '\n\n';

        // Get the parent container of the heading
        const container =
          headingElement.closest('.formatted-text') ||
          headingElement.parentElement;
        let currentElement = headingElement.parentElement.nextElementSibling;

        // Collect content until we hit the next heading or end
        while (currentElement) {
          // Stop if we hit another heading
          if (currentElement.querySelector('h3, .heading-with-copy')) {
            break;
          }

          // Add both HTML and plain text content
          if (currentElement.innerHTML && currentElement.innerHTML.trim()) {
            // Clean up the HTML and preserve styling
            let elementHTML = currentElement.innerHTML;

            // Apply consistent styling for copied content
            elementHTML = elementHTML
              .replace(
                /class="text-yellow-500 font-bold"/g,
                'style="color: #EAB308; font-weight: bold;"'
              )
              .replace(
                /class="timestamp[^"]*"/g,
                'style="color: #6B7280; font-weight: 600; cursor: pointer;"'
              )
              .replace(
                /class="[^"]*text-green-500[^"]*"/g,
                'style="color: #22C55E;"'
              )
              .replace(
                /class="list-disc[^"]*"/g,
                'style="list-style-type: disc; margin-left: 1rem; margin-bottom: 1rem;"'
              )
              .replace(/class="ml-2"/g, 'style="margin-left: 0.5rem;"');

            htmlContent += elementHTML + '\n';
          }

          const textContent =
            currentElement.innerText || currentElement.textContent;
          if (textContent && textContent.trim()) {
            plainTextContent += textContent.trim() + '\n';
          }

          currentElement = currentElement.nextElementSibling;
        }

        // Copy both HTML and plain text to clipboard
        const clipboardData = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([plainTextContent], { type: 'text/plain' }),
        });

        navigator.clipboard
          .write([clipboardData])
          .then(() => {
            // Show success feedback
            this.showCopyFeedback(headingElement);
          })
          .catch((err) => {
            // Fallback to plain text if HTML copy fails
            console.warn('HTML copy failed, falling back to plain text:', err);
            navigator.clipboard
              .writeText(plainTextContent.trim())
              .then(() => {
                this.showCopyFeedback(headingElement);
              })
              .catch((fallbackErr) => {
                console.error('Failed to copy:', fallbackErr);
              });
          });
      } catch (error) {
        console.error('Copy error:', error);
      }
    },
    showCopyFeedback(element) {
      const button = element.parentElement.querySelector('.copy-heading-btn');
      if (button) {
        const originalHTML = button.innerHTML;
        const originalStyle = {
          color: button.style.color,
          opacity: button.style.opacity,
          background: button.style.backgroundColor,
          transform: button.style.transform,
        };

        button.innerHTML =
          '<span style="font-size: 0.75rem; font-weight: 600; white-space: nowrap;">Copied</span>';
        button.style.color = '#22C55E';
        button.style.opacity = '1';
        button.style.backgroundColor = 'rgba(34, 197, 94, 0.15)';
        button.style.transform = 'translateY(-1px)';
        button.style.width = 'auto';
        button.style.minWidth = '60px';
        button.style.padding = '0.3rem 0.6rem';

        setTimeout(() => {
          button.innerHTML = originalHTML;
          button.style.color = originalStyle.color;
          button.style.opacity = originalStyle.opacity;
          button.style.backgroundColor = originalStyle.background;
          button.style.transform = originalStyle.transform;
          button.style.width = '32px';
          button.style.minWidth = '';
          button.style.padding = '0.4rem';
        }, 2000);
      }
    },
  },
};
</script>

<style scoped>
* {
  font-family: 'JetBrains Mono', monospace;
  box-sizing: border-box;
}

.video-container {
  min-height: 100vh;
  background-color: #000000;
  color: #ffffff;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.nav-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 2rem;
  background-color: rgba(0, 0, 0, 0.9);
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid #333;
}

.nav-left {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.nav-center {
  display: flex;
  align-items: center;
  flex: 1;
  max-width: 600px;
  margin: 0 2rem;
}

.global-search {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.global-search-input {
  flex: 1;
  padding: 0.75rem 1rem;
  background-color: #222;
  border: 1px solid #444;
  color: #fff;
  font-size: 0.9rem;
  border-radius: 6px;
  outline: none;
  transition: all 0.2s ease;
}

.global-search-input:focus {
  border-color: #22c55e;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.3);
}

.global-search-input::placeholder {
  color: #888;
}

.global-search-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.global-match-count {
  font-size: 0.8rem;
  color: #22c55e;
  font-weight: 500;
}

.global-navigation-arrows {
  display: flex;
  gap: 0.25rem;
}

.global-arrow-btn {
  background-color: #22c55e;
  color: #000;
  border: none;
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
  font-weight: bold;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.global-arrow-btn:hover {
  background-color: #16a34a;
}

.logo {
  width: 2.5rem;
  height: 2.5rem;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.logo:hover {
  transform: scale(1.1);
}

.recording-title {
  font-size: 1.25rem;
  font-weight: 500;
  color: #ffffff;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 40vw;
}

.share-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background-color: transparent;
  border: 2px solid #22c55e;
  color: #22c55e;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
}

.share-btn:hover {
  background-color: #22c55e;
  color: #000000;
  box-shadow: 0 0 10px rgba(34, 197, 94, 0.5);
}

.share-btn img {
  width: 1rem;
  height: 1rem;
}

/* Main Content Layout */
.main-content {
  flex: 1;
  padding: 1.5rem 2rem;
}

.content-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  grid-template-rows: auto 1fr;
  gap: 1.5rem;
  height: auto;
}

.video-section {
  grid-column: 1;
  grid-row: 1;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.video-player {
  width: 100%;
  height: 100%;
  max-height: 60vh;
  background-color: #000;
  outline: none;
}

.summary-panel {
  grid-column: 2;
  grid-row: 1 / span 2;
  display: flex;
  flex-direction: column;
  height: 87vh;
}

.transcription-panel {
  grid-column: 1;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  height: auto;
  max-height: 80vh;
}

.panel {
  background-color: #111;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid #333;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background-color: #1a1a1a;
  border-bottom: 1px solid #333;
}

.panel-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #d7df23;
  margin: 0;
}

.download-options {
  display: flex;
  gap: 0.75rem;
}

.download-btn {
  width: 1.5rem;
  height: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: 0.7;
}

.download-btn:hover {
  opacity: 1;
  transform: translateY(-2px);
}

/* Search Bar Styles */
.search-bar {
  display: flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  background-color: #1a1a1a;
  border-bottom: 1px solid #333;
  position: relative;
}

.search-bar input {
  flex: 1;
  padding: 0.6rem 1rem;
  background-color: #222;
  border: 1px solid #333;
  color: #ffffff;
  font-size: 0.9rem;
  outline: none;
  transition: all 0.2s ease;
}

.search-bar input:focus {
  border-color: #22c55e;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.3);
}

.search-bar input::placeholder {
  color: #666;
}

.search-controls {
  display: flex;
  align-items: center;
  margin-left: 1rem;
}

.match-count {
  font-size: 0.8rem;
  color: #999;
  margin-right: 0.5rem;
}

.navigation-arrows {
  display: flex;
  gap: 0.25rem;
}

.arrow-btn {
  background-color: #22c55e;
  color: #000;
  border: none;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
}

.arrow-btn:hover {
  background-color: #2ed158;
}

/* Panel Content Styles */
.panel-content {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #22c55e #222;
}

.panel-content::-webkit-scrollbar {
  width: 8px;
}

.panel-content::-webkit-scrollbar-track {
  background: #222;
}

.panel-content::-webkit-scrollbar-thumb {
  background-color: #22c55e;
}

.transcript-content {
  line-height: 1.6;
  font-size: 0.95rem;
}

.formatted-text {
  line-height: 1.6;
}

.formatted-text p {
  margin-bottom: 1rem;
}

.speaker-block {
  margin-bottom: 1.25rem;
}

.speaker-name {
  font-weight: 600;
  color: #d7df23;
  margin-bottom: 0.25rem;
  display: inline-block;
}

.speaker-text {
  color: #eee;
}

.timestamp {
  color: #22c55e;
  font-size: 0.85rem;
  margin-right: 0.5rem;
}

.search-match {
  background-color: #d7df23;
  color: #000;
  padding: 0.1rem 0.2rem;
  font-weight: 500;
}

.focused-match,
.focused-match2 {
  background-color: #d7df23;
  color: #000;
  padding: 0.1rem 0.2rem;
  font-weight: 700;
  box-shadow: 0 0 0 2px #d7df23;
}

.focused-global-match {
  background-color: #22c55e !important;
  color: #000 !important;
  padding: 0.1rem 0.2rem;
  font-weight: 700;
  box-shadow: 0 0 0 3px #22c55e;
  border-radius: 2px;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 70vh;
}

.spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  text-align: center;
  animation: blinkOpacity 1.5s infinite ease-in-out;
}

.spinner-logo {
  width: 4rem;
  height: 4rem;
  opacity: 0.8;
}

.spinner p {
  color: #22c55e;
  font-size: 1.1rem;
  margin: 0;
}

@keyframes blinkOpacity {
  0%,
  100% {
    opacity: 0.8;
  }

  50% {
    opacity: 0.4;
  }
}

/* Responsive Adjustments */
@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: 1.5fr 1fr;
  }
}

@media (max-width: 992px) {
  .content-grid {
    display: flex;
    flex-direction: column;
    height: auto;
  }

  .video-section {
    height: 50vh;
  }

  .panel {
    height: 400px;
  }
}

@media (max-width: 768px) {
  .nav-bar {
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
  }

  .nav-center {
    margin: 0;
    max-width: 100%;
    width: 100%;
  }

  .global-search-input {
    font-size: 0.85rem;
    padding: 0.6rem 0.8rem;
  }

  .global-match-count {
    font-size: 0.75rem;
  }

  .global-arrow-btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }

  .main-content {
    padding: 1rem;
  }

  .recording-title {
    font-size: 1rem;
    max-width: 50vw;
  }
}

.translate-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.75rem;
  padding: 5px;
}

.translate-select {
  background-color: #111;
  color: #fff;
  border: 1px solid #333;
  padding: 0.4rem 0.6rem;
  font-size: 0.9rem;
}

.translate-select:focus {
  outline: none;
  border-color: #22c55e;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.3);
}

.translating-pill {
  font-size: 0.8rem;
  color: #22c55e;
  opacity: 0.9;
}

.heading-with-copy {
  gap: 1rem;
}

.copy-heading-btn {
  color: #666 !important;
  padding: 0.4rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: 0.5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  outline: none;
}

.copy-heading-btn:hover {
  opacity: 1 !important;
  color: #22c55e !important;
  transform: translateY(-2px) !important;
  background-color: rgba(34, 197, 94, 0.1) !important;
}

.copy-heading-btn:active {
  transform: translateY(0) !important;
}

.copy-heading-btn:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.3);
}

.copy-heading-btn svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}
</style>
